import { useRef, useState, type ReactNode } from "react";
import {
  Dimensions,
  Image,
  Linking,
  Modal,
  Platform,
  Pressable,
  Text,
  View,
} from "react-native";
import {
  ChevronRight,
  ExternalLink,
  MoreHorizontal,
  Phone,
  Plus,
  UserRound,
  type LucideIcon,
} from "lucide-react-native";
import { CardSubtitleText, CardTitleText } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import {
  OrganicMiniIcon,
  type OrganicMiniIconKind,
} from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import type { CareTeamMember as ApiCareTeamMember } from "@src/packages/care/hooks/useCareTeam";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type CycleTeamMember = {
  id: string;
  kind: "person" | "organization";
  label: string;
  name: string;
  detail: string;
  organicIcon: OrganicMiniIconKind;
};

type ClinicContact = {
  phone?: string;
  portalUrl?: string;
};

type Props = {
  members: CycleTeamMember[];
  clinicName?: string | null;
  clinicContact?: ClinicContact;
  onEditTeam?: () => void;
  onAddProviderOrClinic?: () => void;
};

const avatarPlaceholder =
  require("@src/apps/moon/assets/images/avatar-placeholder.png") as number;

const TEXT = EMBER.colors.text;
const TEXT_MID = EMBER.colors.textMid;
const TEXT_LIGHT = EMBER.colors.textLight;
const CYCLE_TEAM_MENU_WIDTH = 232;

function getKnownCycleValue(value: string | null | undefined) {
  if (!value || value === "—") return null;
  return value;
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
}

export function buildCycleTeamMembers({
  members,
  clinicName,
  doctorName,
}: {
  members: ApiCareTeamMember[] | undefined;
  clinicName?: string | null;
  doctorName?: string | null;
}): CycleTeamMember[] {
  const knownClinicName = getKnownCycleValue(clinicName);
  const people: CycleTeamMember[] = (members ?? []).map((member) => ({
    id: member.id,
    kind: "person",
    label: member.role || "Provider",
    name: member.name,
    detail: [member.role, member.email, member.phoneNumber, knownClinicName]
      .filter(Boolean)
      .join(" · "),
    organicIcon: "concierge",
  }));

  const knownDoctorName = getKnownCycleValue(doctorName);
  const hasDoctorFromCareTeam = knownDoctorName
    ? people.some(
        (member) =>
          member.name.trim().toLowerCase() ===
          knownDoctorName.trim().toLowerCase(),
      )
    : false;

  if (knownDoctorName && !hasDoctorFromCareTeam) {
    people.unshift({
      id: `doctor-${slugify(knownDoctorName)}`,
      kind: "person",
      label: "REI",
      name: knownDoctorName,
      detail: knownClinicName
        ? `Reproductive endocrinologist · ${knownClinicName}`
        : "Reproductive endocrinologist",
      organicIcon: "concierge",
    });
  }

  if (!knownClinicName) return people;

  return [
    ...people,
    {
      id: `clinic-${slugify(knownClinicName)}`,
      kind: "organization",
      label: "Clinic",
      name: knownClinicName,
      detail:
        people.length > 0
          ? "Clinic connected to this cycle."
          : "Add providers, portal details, and cycle instructions.",
      organicIcon: "clinics",
    },
  ];
}

function translateCycleTeamMember(
  member: CycleTeamMember,
  t: TranslationFunction,
): CycleTeamMember {
  if (member.label === "REI") {
    const trailingClinic = member.detail.includes("·")
      ? ` · ${member.detail.split("·").slice(1).join("·").trim()}`
      : "";
    return {
      ...member,
      label: t("care.cycle_clinic_team.rei"),
      detail: `${t("care.cycle_clinic_team.reproductive_endocrinologist")}${trailingClinic}`,
    };
  }
  if (member.label === "Clinic") {
    const isClinicConnected =
      member.detail === "Clinic connected to this cycle.";
    const isAddProviders =
      member.detail ===
      "Add providers, portal details, and cycle instructions.";
    return {
      ...member,
      label: t("care.cycle_clinic_team.clinic"),
      detail: isClinicConnected
        ? t("care.cycle_clinic_team.clinic_connected")
        : isAddProviders
          ? t("care.cycle_clinic_team.add_providers_portal")
          : member.detail,
    };
  }
  return member;
}

function CycleTeamPersonCard({ member }: { member: CycleTeamMember }) {
  const t = useTranslations();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t("care.cycle_clinic_team.open_member", {
        params: { name: member.name },
      })}
      style={({ pressed }) => ({
        flexGrow: 1,
        flexBasis: "47%",
        minWidth: 128,
        borderRadius: 15,
        paddingHorizontal: 11,
        paddingVertical: 12,
        backgroundColor: "rgba(255,255,255,0.38)",
        borderWidth: 1,
        borderColor: "rgba(92,52,32,0.08)",
        alignItems: "center",
        gap: 8,
        opacity: pressed ? 0.78 : 1,
      })}
    >
      <View
        style={{
          width: 48,
          height: 48,
          borderRadius: 24,
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.54)",
          overflow: "hidden",
          backgroundColor: "rgba(255,255,255,0.26)",
        }}
      >
        <Image
          source={avatarPlaceholder}
          resizeMode="cover"
          style={{ width: 48, height: 48 }}
        />
      </View>
      <View style={{ alignItems: "center", minWidth: 0 }}>
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            fontSize: 13,
            lineHeight: 16,
            color: TEXT,
            fontWeight: "900",
            textAlign: "center",
          }}
        >
          {member.name}
        </Text>
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            fontSize: 10,
            lineHeight: 12,
            color: TEXT_LIGHT,
            fontWeight: "900",
            textTransform: "uppercase",
            letterSpacing: 0.25,
            marginTop: 2,
            textAlign: "center",
          }}
        >
          {member.label}
        </Text>
      </View>
    </Pressable>
  );
}

function CycleTeamOrganizationRow({ member }: { member: CycleTeamMember }) {
  const t = useTranslations();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t("care.cycle_clinic_team.open_member", {
        params: { name: member.name },
      })}
      style={({ pressed }) => ({
        borderRadius: 15,
        padding: 10,
        backgroundColor: "rgba(255,255,255,0.38)",
        borderWidth: 1,
        borderColor: "rgba(92,52,32,0.08)",
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        opacity: pressed ? 0.78 : 1,
      })}
    >
      <OrganicMiniIcon kind={member.organicIcon} size={34} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 10,
            lineHeight: 12,
            color: TEXT_LIGHT,
            fontWeight: "900",
            textTransform: "uppercase",
            letterSpacing: 0.25,
          }}
        >
          {member.label}
        </Text>
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{
            fontSize: 13.5,
            lineHeight: 17,
            color: TEXT,
            fontWeight: "900",
            marginTop: 2,
          }}
        >
          {member.name}
        </Text>
        <Text
          className="font-dm-sans"
          numberOfLines={2}
          style={{
            fontSize: 11,
            lineHeight: 14,
            color: TEXT_MID,
            fontWeight: "600",
            marginTop: 1,
          }}
        >
          {member.detail}
        </Text>
      </View>
      <ChevronRight size={14} color={TEXT_LIGHT} strokeWidth={1.8} />
    </Pressable>
  );
}

function CycleTeamMenuButton({
  onEditTeam,
  onAddProviderOrClinic,
}: {
  onEditTeam: () => void;
  onAddProviderOrClinic: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CycleTeamMenuButton");
  const buttonRef = useRef<View>(null);
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(
    null,
  );

  const openMenu = () => {
    capturePress(
      pressLabel(t("care.cycle_clinic_team.open_actions")),
      "care.cycle_clinic_team.open_actions",
    );
    buttonRef.current?.measureInWindow((x, y, width, height) => {
      const screenWidth = Dimensions.get("window").width;
      setAnchor({
        top: y + height + 8,
        right: Math.max(10, screenWidth - (x + width)),
      });
    });
  };

  const closeMenu = () => setAnchor(null);

  const menuItems: {
    label: string;
    icon: LucideIcon;
    onPress: () => void;
  }[] = [
    {
      label: t("care.cycle_clinic_team.menu_edit_team"),
      icon: UserRound,
      onPress: onEditTeam,
    },
    {
      label: t("care.cycle_clinic_team.menu_add_provider"),
      icon: Plus,
      onPress: onAddProviderOrClinic,
    },
  ];

  return (
    <>
      <Pressable
        ref={buttonRef}
        onPress={openMenu}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel={t("care.cycle_clinic_team.open_actions")}
        style={({ pressed }) => ({
          width: 30,
          height: 30,
          borderRadius: 15,
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          opacity: pressed ? 0.62 : 1,
        })}
      >
        <MoreHorizontal size={19} color={TEXT_LIGHT} strokeWidth={2} />
      </Pressable>

      <Modal
        visible={anchor !== null}
        transparent
        animationType="fade"
        onRequestClose={closeMenu}
      >
        <Pressable
          onPress={() => {
            capturePress("dismiss_backdrop");
            closeMenu();
          }}
          style={{ flex: 1 }}
        >
          {anchor ? (
            <Pressable
              onPress={(event) => event.stopPropagation()}
              style={{
                position: "absolute",
                top: anchor.top,
                right: anchor.right,
                width: CYCLE_TEAM_MENU_WIDTH,
                borderRadius: 14,
                overflow: "hidden",
                backgroundColor:
                  Platform.OS === "web"
                    ? "rgba(255,255,255,0.92)"
                    : "rgba(255,255,255,0.98)",
                borderWidth: 1,
                borderColor: "rgba(0,0,0,0.08)",
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: 0.14,
                shadowRadius: 24,
                ...(Platform.OS === "web"
                  ? ({
                      backdropFilter: "blur(20px)",
                      WebkitBackdropFilter: "blur(20px)",
                    } as Record<string, string>)
                  : null),
              }}
            >
              {menuItems.map((item, index) => {
                const Icon = item.icon;

                return (
                  <Pressable
                    key={item.label}
                    onPress={() => {
                      // Every menu item funnels through here; its label is
                      // translated copy, so pair it with a stable action.
                      capturePress(
                        pressLabel(item.label),
                        "cycle_team_menu_item",
                      );
                      closeMenu();
                      item.onPress();
                    }}
                    style={({ pressed }) => ({
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 10,
                      paddingHorizontal: 14,
                      paddingVertical: 12,
                      borderTopWidth: index === 0 ? 0 : 1,
                      borderTopColor: "rgba(0,0,0,0.055)",
                      backgroundColor: pressed
                        ? "rgba(0,0,0,0.04)"
                        : "transparent",
                    })}
                  >
                    <Icon size={16} color={TEXT_MID} strokeWidth={1.8} />
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        lineHeight: 18,
                        color: TEXT,
                        fontWeight: "600",
                      }}
                    >
                      {item.label}
                    </Text>
                  </Pressable>
                );
              })}
            </Pressable>
          ) : null}
        </Pressable>
      </Modal>
    </>
  );
}

function TeamSetupAction({
  icon,
  title,
  subtitle,
  onPress,
  comingSoon,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  onPress?: () => void;
  comingSoon?: boolean;
}) {
  const capturePress = usePressCapture("TeamSetupAction");
  return (
    <Pressable
      onPress={
        comingSoon || !onPress
          ? undefined
          : () => {
              // `title` is translated copy supplied by the caller.
              capturePress(pressLabel(title), "team_setup_action");
              onPress();
            }
      }
      accessibilityRole="button"
      accessibilityLabel={title}
      style={({ pressed }) => ({
        borderRadius: 14,
        borderWidth: 1,
        borderColor: "rgba(92,52,32,0.08)",
        backgroundColor: "rgba(255,255,255,0.34)",
        paddingHorizontal: 12,
        paddingVertical: 11,
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        opacity: comingSoon ? 0.5 : pressed ? 0.76 : 1,
      })}
    >
      {icon}
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 16,
            color: TEXT,
            fontWeight: "900",
          }}
        >
          {title}
        </Text>
        <CardSubtitleText>{subtitle}</CardSubtitleText>
      </View>
      <ChevronRight size={14} color={TEXT_LIGHT} strokeWidth={1.8} />
    </Pressable>
  );
}

export default function CycleClinicTeamCard({
  members,
  clinicName,
  clinicContact = {},
  onEditTeam,
  onAddProviderOrClinic,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("CycleClinicTeamCard");
  const translatedMembers = members.map((member) =>
    translateCycleTeamMember(member, t),
  );
  const people = translatedMembers.filter((member) => member.kind === "person");
  const organizations = translatedMembers.filter(
    (member) => member.kind === "organization",
  );
  const hasTeamDetails =
    people.length > 0 || organizations.length > 0 || Boolean(clinicName);
  const displayClinicName =
    clinicName ?? t("care.cycle_clinic_team.connect_care_team_fallback");
  const showActions = Boolean(onEditTeam && onAddProviderOrClinic);

  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as Record<string, unknown>),
        borderRadius: 18,
        padding: 14,
        gap: 12,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 12 }}>
        <OrganicMiniIcon kind="clinics" size={42} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <View style={{ flex: 1, minWidth: 0 }}>
              {hasTeamDetails ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 10,
                    lineHeight: 12,
                    color: TEXT_LIGHT,
                    fontWeight: "900",
                    textTransform: "uppercase",
                    letterSpacing: 0.25,
                  }}
                >
                  {t("care.cycle_clinic_team.clinic")}
                </Text>
              ) : null}
              <CardTitleText numberOfLines={1} style={{ marginTop: 2 }}>
                {displayClinicName}
              </CardTitleText>
            </View>
            {hasTeamDetails ? (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 4,
                  marginTop: -2,
                }}
              >
                {clinicContact.phone ? (
                  <Pressable
                    onPress={() => {
                      capturePress(
                        pressLabel(t("care.cycle_clinic_team.call_clinic")),
                        "care.cycle_clinic_team.call_clinic",
                      );
                      void Linking.openURL(`tel:${clinicContact.phone}`);
                    }}
                    hitSlop={8}
                    accessibilityRole="button"
                    accessibilityLabel={t("care.cycle_clinic_team.call_clinic")}
                    style={({ pressed }) => ({
                      width: 30,
                      height: 30,
                      borderRadius: 15,
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: pressed ? 0.62 : 1,
                    })}
                  >
                    <Phone size={17} color={TEXT_LIGHT} strokeWidth={1.9} />
                  </Pressable>
                ) : null}
                {clinicContact.portalUrl ? (
                  <Pressable
                    onPress={() => {
                      capturePress(
                        pressLabel(t("care.cycle_clinic_team.open_portal")),
                        "care.cycle_clinic_team.open_portal",
                      );
                      if (clinicContact.portalUrl) {
                        void Linking.openURL(clinicContact.portalUrl);
                      }
                    }}
                    hitSlop={8}
                    accessibilityRole="button"
                    accessibilityLabel={t("care.cycle_clinic_team.open_portal")}
                    style={({ pressed }) => ({
                      width: 30,
                      height: 30,
                      borderRadius: 15,
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: pressed ? 0.62 : 1,
                    })}
                  >
                    <ExternalLink
                      size={16}
                      color={TEXT_LIGHT}
                      strokeWidth={1.9}
                    />
                  </Pressable>
                ) : null}
                {showActions && onEditTeam && onAddProviderOrClinic ? (
                  <CycleTeamMenuButton
                    onEditTeam={onEditTeam}
                    onAddProviderOrClinic={onAddProviderOrClinic}
                  />
                ) : null}
              </View>
            ) : null}
          </View>
          <CardSubtitleText>
            {hasTeamDetails
              ? t("care.cycle_clinic_team.team_subtitle_present")
              : t("care.cycle_clinic_team.team_subtitle_empty")}
          </CardSubtitleText>
        </View>
      </View>

      <View style={{ gap: 8 }}>
        {hasTeamDetails ? (
          <>
            {people.length > 0 ? (
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                {people.map((member) => (
                  <CycleTeamPersonCard key={member.id} member={member} />
                ))}
              </View>
            ) : null}
            {organizations.map((member) => (
              <CycleTeamOrganizationRow key={member.id} member={member} />
            ))}
          </>
        ) : (
          <>
            <TeamSetupAction
              icon={<OrganicMiniIcon kind="clinics" size={30} />}
              title={t("care.cycle_clinic_team.connect_clinic_title")}
              subtitle={t("care.cycle_clinic_team.connect_clinic_subtitle")}
              onPress={onAddProviderOrClinic}
              comingSoon={!onAddProviderOrClinic}
            />
            <TeamSetupAction
              icon={<OrganicMiniIcon kind="concierge" size={30} />}
              title={t("care.cycle_clinic_team.add_physicians_title")}
              subtitle={t("care.cycle_clinic_team.add_physicians_subtitle")}
              onPress={onAddProviderOrClinic}
              comingSoon={!onAddProviderOrClinic}
            />
          </>
        )}
      </View>
    </View>
  );
}
